
import React, { useState } from "react";
import { Control } from "react-hook-form";
import { Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

interface PasswordFieldProps {
  control: Control<any>;
  name: string;
  label: string;
  placeholder?: string;
  autoComplete?: string;
  helpText?: string;
  required?: boolean;
}

const PasswordField = ({
  control,
  name,
  label,
  placeholder = "Enter your password",
  autoComplete = "current-password",
  helpText,
  required = false
}: PasswordFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");

  // Password validation checks
  const hasLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  // Show validation checklist only for register forms
  const showChecklist = name === "password" && autoComplete === "new-password";

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel required={required}>{label}</FormLabel>
          <FormControl>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder={placeholder}
                autoComplete={autoComplete}
                className="h-11 pr-10"
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setPassword(e.target.value);
                }}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </FormControl>
          {showChecklist && (
            <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-100">
              <p className="text-xs text-gray-600 mb-2 font-medium">Password must contain:</p>
              <ul className="space-y-1 text-xs">
                <PasswordRequirement met={hasLength} text="At least 8 characters" />
                <PasswordRequirement met={hasUppercase} text="At least one uppercase letter (A-Z)" />
                <PasswordRequirement met={hasLowercase} text="At least one lowercase letter (a-z)" />
                <PasswordRequirement met={hasNumber} text="At least one number (0-9)" />
                <PasswordRequirement met={hasSpecial} text="At least one special character (!@#$%^&*)" />
              </ul>
            </div>
          )}
          {helpText && !showChecklist && (
            <div className="text-xs text-gray-500 mt-1">{helpText}</div>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

// Helper component for each password requirement
const PasswordRequirement = ({ met, text }: { met: boolean; text: string }) => (
  <li className="flex items-center gap-2">
    {met ? (
      <CheckCircle2 className="h-3 w-3 text-green-500" />
    ) : (
      <XCircle className="h-3 w-3 text-gray-400" />
    )}
    <span className={met ? "text-gray-700" : "text-gray-500"}>{text}</span>
  </li>
);

export default PasswordField;
